<template>
  <v-container>
    <v-dialog v-model="deleteDialog" max-width="300px">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{ $t('Tem certeza de que deseja excluir esse item?') }}
          </div>

          <v-btn
            :disabled="deleteLoading"
            class="ma-1"
            color="grey"
            text
            @click="deleteDialog = false"
          >
            Cancelar
          </v-btn>

          <v-btn
            class="ma-1"
            color="error"
            text
            :loading="deleteLoading"
            @click="removeCalibration"
          >
            {{ $t('Excluir') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>

    <v-dialog v-model="newRangeModal" max-width="900">
      <ManageRange
        :key="newRangeModalKey"
        :parameter="currentParameter"
        :patterns="patterns"
        @success="addRangeToParameter"
        @close="newRangeModal = false"
      />
    </v-dialog>

    <v-dialog v-model="confirmDialog.visible" max-width="300">
      <v-card class="pa-4 text-center">
        <strong>{{ confirmDialog.title }}</strong>
        <p>{{ confirmDialog.message }}</p>
        <div class="d-flex">
          <v-btn
            outlined
            small
            height="40"
            style="border-radius: 10px"
            color="#000"
            @click="confirmDialog.onCancel"
          >
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="primary-button ma-0"
            small
            color="red"
            depressed
            @click="confirmDialog.onConfirm"
          >
            Confirmar
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-expand-transition>
      <v-card
        v-if="order.category == 1"
        class="pa-4 ma-2 mb-4"
        dark
        rounded="lg"
        flat
        outlined
        color="red"
      >
        <strong>
          Exclusivo para ordens de serviço da categoria "Equipamento"
        </strong>
      </v-card>
    </v-expand-transition>

    <v-form ref="form" :disabled="disableForm" :lazy-validation="true">
      <v-card
        class="pa-4 ma-2 mb-4 d-flex justify-center"
        rounded="lg"
        flat
        outlined
      >
        <v-btn left absolute small text color="grey" @click="$router.go(-1)">
          <v-icon x-small class="mr-1">mdi-arrow-left-bold</v-icon>
          Voltar
        </v-btn>
        <h3><strong>Calibração (V2)</strong></h3>
      </v-card>

      <v-card class="ma-2 my-4" rounded="lg" flat outlined>
        <div class="pa-4 d-flex align-center">
          <strong>Dados do contratante</strong>
          <v-spacer></v-spacer>
          <v-btn small icon @click="showContractorData = !showContractorData">
            <v-icon>
              {{ showContractorData ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </div>

        <v-divider v-if="showContractorData"></v-divider>

        <v-expand-transition>
          <div class="pa-2" v-if="showContractorData">
            <v-row class="ma-0">
              <v-col cols="12" sm="4">
                <label class="label">Instituição</label>
                <v-autocomplete
                  v-model="order.institutionId"
                  class="primary-input"
                  outlined
                  :loading="institutionLoading"
                  :items="institutions"
                  item-value="id"
                  item-text="name"
                  disabled
                  hide-details=""
                  placeholder="Selecione a instituição"
                  append-icon="mdi-chevron-down"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="label">Data da calibração / Certificado</label>

                <date-picker
                  ref="datepicker"
                  style="width: 100%"
                  v-model="date"
                  mode="dateTime"
                  is24hr
                  :popover="{ visibility: 'click', positionFixed: 'true' }"
                >
                  <template v-slot="{ inputValue, inputEvents }">
                    <v-text-field
                      outlined
                      class="primary-input"
                      prepend-inner-icon="mdi-calendar"
                      :value="inputValue"
                      v-on="inputEvents"
                      hide-details="auto"
                      :rules="[(v) => !!v || 'Campo obrigatório']"
                      placeholder="Selecione a data"
                      v-mask="['##/##/#### ##:##']"
                      @focus="setNowDate"
                    />
                  </template>
                </date-picker>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="label">Próxima calibração</label>
                <v-menu
                  ref="nextCalibrationDate"
                  v-model="nextCalibrationDateMenu"
                  :close-on-content-click="false"
                  :return-value.sync="calibration.nextCalibrationDate"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      outlined
                      :value="formatMonth(calibration.nextCalibrationDate)"
                      class="primary-input"
                      readonly
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="calibration.nextCalibrationDate"
                    type="month"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      @click="nextCalibrationDateMenu = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="
                        $refs.nextCalibrationDate.save(
                          calibration.nextCalibrationDate
                        )
                      "
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card>

      <v-card class="ma-2 my-4" rounded="lg" flat outlined>
        <div class="pa-4 d-flex align-center">
          <strong>Instrumento / Equipamento Calibrado</strong>
          <v-spacer></v-spacer>
          <v-btn small icon @click="showEquipmentData = !showEquipmentData">
            <v-icon>
              {{ showEquipmentData ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </div>

        <v-divider v-if="showEquipmentData"></v-divider>

        <v-expand-transition>
          <div class="pa-2" v-if="showEquipmentData">
            <v-row class="ma-0">
              <v-col cols="12" sm="4">
                <label class="label">Equipamento</label>
                <v-text-field
                  v-model="equipment.name"
                  class="primary-input"
                  outlined
                  append-icon="mdi-chevron-down"
                  placeholder="Selecione o equipamento"
                  hide-details
                  disabled
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="label">Modelo</label>
                <v-text-field
                  v-model="equipment.model"
                  class="primary-input"
                  outlined
                  hide-details
                  disabled
                  placeholder="Selecione o equipamento"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="label">Identificação</label>
                <v-text-field
                  v-model="equipment.identification"
                  class="primary-input"
                  outlined
                  hide-details
                  disabled
                  placeholder="Selecione o equipamento"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="label">Nº série</label>
                <v-text-field
                  v-model="equipment.serialNumber"
                  class="primary-input"
                  outlined
                  hide-details
                  disabled
                  placeholder="Selecione o equipamento"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="label">Patrimônio</label>
                <v-text-field
                  v-model="equipment.propertyCode"
                  class="primary-input"
                  outlined
                  hide-details
                  disabled
                  placeholder="Selecione o equipamento"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="label">Fabricante</label>
                <v-text-field
                  v-model="equipment.manufacturer"
                  class="primary-input"
                  outlined
                  hide-details
                  disabled
                  placeholder="Selecione o equipamento"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card>

      <v-card class="ma-2 my-4" rounded="lg" flat outlined>
        <div class="pa-4 d-flex align-center">
          <strong>Condições Ambientais</strong>
          <v-spacer></v-spacer>
          <v-btn
            small
            icon
            @click="showAmbientalConditionsData = !showAmbientalConditionsData"
          >
            <v-icon>
              {{
                showAmbientalConditionsData
                  ? 'mdi-chevron-up'
                  : 'mdi-chevron-down'
              }}
            </v-icon>
          </v-btn>
        </div>

        <v-divider v-if="showAmbientalConditionsData"></v-divider>

        <v-expand-transition>
          <div class="pa-2" v-if="showAmbientalConditionsData">
            <v-row class="ma-0">
              <v-col>
                <label class="label">Temperatura</label>
                <v-text-field
                  v-model="calibration.temperature"
                  :rules="[(v) => !!v || 'Campo obrigatório']"
                  class="primary-input"
                  outlined
                  placeholder="Insira a temperatura"
                ></v-text-field>
              </v-col>
              <v-col>
                <label class="label">Umidade relativa do ar</label>
                <v-text-field
                  v-model="calibration.relativeHumidity"
                  :rules="[(v) => !!v || 'Campo obrigatório']"
                  class="primary-input"
                  outlined
                  placeholder="Insira a umidade relativa do ar"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card>

      <v-card class="ma-2 my-4" rounded="lg" flat outlined>
        <div class="pa-4 d-flex align-center">
          <strong>Informações adicionais</strong>
          <v-spacer></v-spacer>
          <v-btn
            small
            icon
            @click="
              showAdditionalInformationData = !showAdditionalInformationData
            "
          >
            <v-icon>
              {{
                showAdditionalInformationData
                  ? 'mdi-chevron-up'
                  : 'mdi-chevron-down'
              }}
            </v-icon>
          </v-btn>
        </div>

        <v-divider v-if="showAdditionalInformationData"></v-divider>

        <v-expand-transition>
          <div class="pa-2" v-if="showAdditionalInformationData">
            <v-row class="ma-0">
              <v-col>
                <label class="label">Rodapé</label>
                <v-textarea
                  v-model="calibration.footnote"
                  class="primary-input"
                  outlined
                  rows="3"
                ></v-textarea>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card>

      <v-card v-if="isEditing" class="ma-2 my-4" rounded="lg" flat outlined>
        <div
          class="pa-4 d-flex align-center"
          style="cursor: pointer"
          @click="showActionsData = !showActionsData"
        >
          <strong>Ações</strong>
          <v-spacer></v-spacer>
          <v-btn small icon>
            <v-icon>
              {{ showActionsData ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </div>

        <v-divider v-if="showActionsData"></v-divider>

        <v-expand-transition>
          <div class="pa-2" v-if="showActionsData">
            <v-row class="ma-0">
              <v-col cols="12" class="d-flex justify-center">
                <v-btn
                  class="ma-1 secondary-button"
                  depressed
                  :to="`/calibration/v2/print?id=${$route.query.id}`"
                  target="_blank"
                >
                  <v-icon left>mdi-printer</v-icon>
                  Ver certificado
                </v-btn>
                <v-btn
                  class="ma-1 rounded-lg primary-button"
                  depressed
                  @click="enableEdit = !enableEdit"
                >
                  <v-icon left>
                    {{ enableEdit ? 'mdi-lock' : 'mdi-pencil-outline' }}
                  </v-icon>
                  {{
                    enableEdit
                      ? 'Desabilitar edição'
                      : 'Editar valores certificado'
                  }}
                </v-btn>
                <v-btn
                  class="ma-1 rounded-lg"
                  depressed
                  color="#A42A2AD9"
                  @click="deleteCalibration"
                >
                  <v-icon left>mdi-trash-can-outline</v-icon>
                  Excluir dados calibração
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card>

      <template v-if="enableEdit || !isEditing">
        <v-card class="ma-2 my-4" rounded="lg" flat outlined>
          <div class="pa-4 d-flex align-center">
            <strong>Template</strong>
            <v-spacer></v-spacer>
            <v-btn small icon @click="showTemplateData = !showTemplateData">
              <v-icon>
                {{ showTemplateData ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </v-btn>
          </div>

          <v-divider v-if="showTemplateData"></v-divider>

          <v-expand-transition>
            <div class="pa-2" v-if="showTemplateData">
              <v-row class="ma-0">
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="calibration.templateId"
                    class="primary-input"
                    :items="templates"
                    item-value="id"
                    item-text="name"
                    :loading="templateLoading"
                    outlined
                    placeholder="Selecione o template"
                    append-icon="mdi-chevron-down"
                    hide-details="auto"
                    :rules="[(v) => !!v || 'Campo obrigatório']"
                    @change="getParameters"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card>

        <v-expand-transition>
          <v-card
            class="ma-2 my-4"
            rounded="lg"
            flat
            outlined
            v-if="calibration.templateId"
          >
            <div class="pa-4 d-flex align-center">
              <strong>Dados da calibração</strong>
              <v-spacer></v-spacer>
              <v-btn
                small
                icon
                @click="showCalibrationData = !showCalibrationData"
              >
                <v-icon>
                  {{
                    showCalibrationData ? 'mdi-chevron-up' : 'mdi-chevron-down'
                  }}
                </v-icon>
              </v-btn>
            </div>

            <div
              v-if="parametersLoading"
              class="d-flex justify-center align-center pa-4"
            >
              <v-progress-circular indeterminate></v-progress-circular>
            </div>

            <div v-else>
              <v-divider v-if="showCalibrationData"></v-divider>

              <v-expand-transition>
                <div class="pa-2" v-if="showCalibrationData">
                  <v-row
                    v-for="parameter in parameters"
                    :key="parameter.id"
                    class="ma-0"
                    justify="space-between"
                  >
                    <v-col cols="12" sm="4" md="4">
                      <label class="label">Padrão</label>
                      <div class="d-flex align-center">
                        <v-autocomplete
                          v-model="parameter.patternId"
                          class="primary-input"
                          :items="patterns"
                          item-text="name"
                          item-value="id"
                          :loading="patternLoading"
                          outlined
                          hide-details="auto"
                          placeholder="Selecione o padrão"
                          append-icon="mdi-chevron-down"
                          :rules="
                            parameters.some((x) => x.patternId != null)
                              ? []
                              : [(v) => !!v || 'Campo obrigatório']
                          "
                          @change="getInterpolatedRanges(parameter)"
                        >
                          <template v-slot:item="{ item }">
                            <span class="text-caption">
                              {{ item.name }} / {{ item.serialNumber }}
                            </span>
                          </template>
                        </v-autocomplete>

                        <v-menu>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              :disabled="
                                !parameter.interpolatedRanges ||
                                parameter.interpolatedRanges.length == 0
                              "
                              icon
                              class="ml-4 mb-3"
                            >
                              <v-icon> mdi-information-outline </v-icon>
                            </v-btn>
                          </template>
                          <v-list nav>
                            <v-subheader>Informações adicionais:</v-subheader>
                            <v-list-item
                              v-for="(range, i) in parameter.interpolatedRanges"
                              :key="i"
                              class="d-block"
                            >
                              <div>
                                <span class="mr-2">
                                  Faixa: <b>{{ range.range }}</b>
                                </span>
                                <span class="mx-2">
                                  Incerteza: <b>{{ range.uncertainty }}</b>
                                </span>
                                <span class="ml-2">
                                  Tendência:
                                  <b>{{
                                    range.originalTendency
                                      ? range.originalTendency
                                      : range.tendency
                                  }}</b>
                                </span>
                              </div>
                              <v-divider></v-divider>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </v-col>

                    <v-col
                      cols="12"
                      sm="4"
                      md="4"
                      lg="3"
                      class="d-flex align-center justify-center font-weight-bold"
                    >
                      {{ parameter.name }}
                      ({{ parameter.metric.unit }})
                    </v-col>

                    <v-col cols="12" sm="4" md="4" lg="3">
                      <label class="label">Resolução equipamento</label>
                      <v-text-field
                        v-model="parameter.equipmentResolution"
                        class="primary-input"
                        outlined
                        hide-details="auto"
                        placeholder="Insira a resolução do equipamento"
                        :rules="
                          parameter.patternId
                            ? [(v) => !!v || 'Campo obrigatório']
                            : []
                        "
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" class="pt-0">
                      <v-simple-table class="main-table">
                        <thead>
                          <tr>
                            <th class="text-center"></th>
                            <th class="text-center">Tolerância</th>
                            <th class="text-center">Faixa</th>
                            <th class="text-center">VL 1</th>
                            <th class="text-center">VL 2</th>
                            <th class="text-center">VL 3</th>
                            <th class="text-center">VL 4</th>
                            <th class="text-center">VL 5</th>
                            <th class="text-center"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="parameter.interpolatedRanges">
                            <td
                              :rowspan="parameter.interpolatedRanges.length + 1"
                              class="text-center"
                            ></td>

                            <td
                              :rowspan="parameter.interpolatedRanges.length + 1"
                              class="text-center"
                            >
                              {{ parameter.tolerance }}
                              {{ parameter.isAbsoluteValue ? '' : '%' }}
                            </td>

                            <td
                              v-if="
                                !parameter.interpolatedRanges ||
                                parameter.interpolatedRanges.length == 0
                              "
                              :colspan="7"
                              class="text-center"
                            >
                              <strong>
                                Selecione o padrão para ver as faixas
                              </strong>
                            </td>
                          </tr>

                          <tr
                            v-for="(range, i) in parameter.interpolatedRanges"
                            :key="i"
                          >
                            <td
                              class="text-center pt-4"
                              style="font-weight: normal !important"
                            >
                              {{ range.range }}
                            </td>
                            <td class="text-center pt-4">
                              <v-text-field
                                v-model="range.vl1"
                                placeholder="---"
                                class="primary-input"
                                style="max-width: 160px; margin: 0 auto"
                                outlined
                                hide-details="auto"
                                :rules="[() => validateRow(range, 'vl1')]"
                              ></v-text-field>
                            </td>
                            <td class="text-center pt-4">
                              <v-text-field
                                v-model="range.vl2"
                                placeholder="---"
                                class="primary-input"
                                style="max-width: 160px; margin: 0 auto"
                                outlined
                                hide-details="auto"
                                :rules="[() => validateRow(range, 'vl2')]"
                              ></v-text-field>
                            </td>
                            <td class="text-center pt-4">
                              <v-text-field
                                v-model="range.vl3"
                                placeholder="---"
                                class="primary-input"
                                style="max-width: 160px; margin: 0 auto"
                                outlined
                                hide-details="auto"
                                :rules="[() => validateRow(range, 'vl3')]"
                              ></v-text-field>
                            </td>
                            <td class="text-center pt-4">
                              <v-text-field
                                v-model="range.vl4"
                                placeholder="---"
                                class="primary-input"
                                style="max-width: 160px; margin: 0 auto"
                                outlined
                                hide-details="auto"
                                :rules="[() => validateRow(range, 'vl4')]"
                              ></v-text-field>
                            </td>
                            <td
                              class="text-center pt-4"
                              style="position: relative"
                            >
                              <v-text-field
                                v-model="range.vl5"
                                placeholder="---"
                                class="primary-input"
                                style="max-width: 160px; margin: 0 auto"
                                outlined
                                hide-details="auto"
                                :rules="[() => validateRow(range, 'vl5')]"
                              ></v-text-field>
                            </td>
                            <td
                              class="text-center pt-4"
                              style="max-width: 40px !important"
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    color="red"
                                    @click="removeRange(parameter, i)"
                                  >
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                </template>
                                <span>Remover faixa</span>
                              </v-tooltip>

                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    v-if="
                                      i ==
                                      parameter.interpolatedRanges.length - 1
                                    "
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    class="mr-0"
                                    color="#4c6d97"
                                    @click="openNewRangeModal(parameter)"
                                  >
                                    <v-icon small>mdi-plus</v-icon>
                                  </v-btn>
                                </template>
                                <span>Nova faixa</span>
                              </v-tooltip>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </div>
          </v-card>
        </v-expand-transition>
      </template>

      <div class="ma-4 d-flex">
        <v-btn
          class="quaternary-button ma-0"
          small
          color="#B14949"
          depressed
          @click="$router.go(-1)"
        >
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          class="primary-button ma-0"
          depressed
          :loading="loading"
          @click="submit"
        >
          Salvar
        </v-btn>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import ApiService from '../../../../../services/ApiService'
import ManageRange from './manageRange.vue'
import moment from 'moment'
import Utils from '../../../../../Utils/FormatDate'

export default {
  name: 'CalibrationV2Manage',

  data() {
    return {
      deleteLoading: false,
      deleteDialog: false,
      enableEdit: false,
      showActionsData: true,
      nextCalibrationDateMenu: false,
      newRangeModalKey: false,
      newRangeModal: false,
      apiService: new ApiService(),
      showContractorData: true,
      showEquipmentData: true,
      showAmbientalConditionsData: true,
      showTemplateData: true,
      showAdditionalInformationData: true,
      showCalibrationData: true,
      calibration: {
        date: null,
        nextCalibrationDate: '',
        temperature: `25,00 ${String.fromCharCode(177)} 0,50 C`,
        relativeHumidity: `55,00 ${String.fromCharCode(177)} 5,00 %`,
      },
      loading: false,
      disableForm: false,
      order: {},
      equipment: {},
      institutionLoading: false,
      institutions: [],
      templateLoading: false,
      templates: [],
      patternLoading: false,
      patterns: [],
      parametersLoading: false,
      parameters: [],
      currentParameter: [],
      interpolationLoading: false,
      interpolatedRanges: [],
      isEditing: false,
      date: null,
      confirmDialog: {
        visible: false,
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
      },
    }
  },

  components: {
    ManageRange,
  },

  created() {
    this.getInstitutions()

    if (this.$route.query.id) {
      this.isEditing = true
      this.getCalibration()
    } else this.getOrder()
  },

  methods: {
    removeCalibration() {
      this.deleteLoading = true
      this.apiService
        .delete(`calibration/v2/certified/${this.$route.query.id}`)
        .then(() => {
          this.deleteLoading = false
          this.deleteDialog = false

          this.$router.push('/calibration/v2')
        })
        .catch(() => {
          this.deleteLoading = false
        })
    },

    deleteCalibration() {
      this.deleteDialog = true
    },

    setNowDate() {
      this.date = !this.date ? new Date() : this.date
    },

    setNextDate() {
      this.calibration.nextCalibrationDate = moment(this.date)
        .add(1, 'y')
        .format('yyyy-MM-DD')
    },

    formatMonth(date) {
      return date ? moment(date).format('MM/YYYY') : ''
    },

    getCalibration() {
      this.loading = true
      this.apiService
        .get(`calibration/v2/certified/${this.$route.query.id}`)
        .then((response) => {
          this.calibration = response.content
          this.date = new Date(this.calibration.date)
          this.parameters = this.calibration.parameters.map((x) => {
            return {
              ...x.parameterJson,
              ...x,
              metric: x.metricJson,
              interpolatedRanges: x.ranges,
            }
          })

          this.$route.query.orderId = this.calibration.serviceOrderId
          this.getOrder()

          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    validateRow(range, key) {
      const fieldsToValidate = ['vl1', 'vl2', 'vl3', 'vl4', 'vl5']

      if (range[key]) return true

      const filledValues = fieldsToValidate
        .map((field) => range[field])
        .filter(
          (value) => value !== undefined && value !== null && value !== ''
        )

      if (filledValues.length === 0) return true

      if (filledValues.length >= 3) return true

      return 'Preencha pelo menos 3 valores nesta linha.'
    },

    addRangeToParameter(range) {
      const parameter = this.parameters.find(
        (x) => x.id == this.currentParameter.id
      )

      if (!parameter) {
        this.$toast.warning(
          'Ocorreu um erro ao adicionar a métrica, tente novamente'
        )
        return
      }

      parameter.interpolatedRanges.push(range)
      this.newRangeModal = false

      this.newRangeModalKey = !this.newRangeModalKey
    },

    openNewRangeModal(parameter) {
      if (!parameter.equipmentResolution) {
        this.$toast.warning('Favor inserir a resolução do equipamento')
        return
      }

      this.currentParameter = parameter
      this.newRangeModalKey = !this.newRangeModalKey
      this.newRangeModal = true
    },

    getInterpolatedRanges(parameter) {
      if (!this.calibration.templateId) return
      if (!parameter.patternId) return

      this.interpolationLoading = true

      this.apiService
        .get(
          `calibration/v2/template/parameter/interpolated/${parameter.id}/${parameter.patternId}`
        )
        .then((response) => {
          parameter.patternResolution = response.content.patternResolution
          parameter.interpolatedRanges = response.content.ranges
          this.interpolationLoading = false
        })
        .catch(() => {
          this.interpolationLoading = false
        })
    },

    getParameters() {
      if (!this.calibration.templateId) return

      this.parametersLoading = true

      this.apiService
        .get(
          `calibration/v2/template/parameter/by-template/${this.calibration.templateId}`
        )
        .then((response) => {
          this.parameters = response.content.map((x) => {
            return {
              ...x,
              interpolatedRanges: [],
              patternResolution: null,
            }
          })
          this.parametersLoading = false
        })
        .catch(() => {
          this.parametersLoading = false
        })
    },

    getTemplates() {
      this.templateLoading = true
      this.apiService
        .get(
          'calibration/v2/template/by-institutionStandardEquipment/' +
            this.equipment.institutionStandardEquipmentId
        )
        .then((response) => {
          this.templates = response.content
          this.templateLoading = false
        })
        .catch(() => {
          this.templateLoading = false
        })
    },

    getPatterns() {
      this.patternLoading = true

      let url = `calibration/v2/pattern/list/${this.order.institutionId}`
      if (this.$route.query.id) url += `?calibrationId=${this.$route.query.id}`

      this.apiService
        .get(url)
        .then((response) => {
          this.patterns = response.content
          this.patternLoading = false
        })
        .catch(() => {
          this.patternLoading = false
        })
    },

    setCalibrationDateToNow() {
      this.calibrationDateTime = !this.calibrationDateTime
        ? new Date()
        : this.calibrationDateTime
    },

    getInstitutions() {
      this.institutionLoading = true
      this.apiService
        .get(`institution/list`)
        .then((response) => {
          this.institutions = response.content
          this.institutionLoading = false
        })
        .catch(() => {
          this.institutionLoading = false
        })
    },

    getOrder() {
      if (this.$route.query.orderId)
        this.apiService
          .get('serviceOrder/by-id/' + this.$route.query.orderId)
          .then((response) => {
            this.order = response.content
            this.calibration.footnote = this.order.footnote
              ? this.order.footnote
              : `EQUIPACARE ENGENHARIA HOSPITALAR LTDA.\nCNPJ:09.642.197/0001-37 - Rua 159, N°124 - Volta Redonda - RJ\nTelefone: (24) 3348-7157. E-mail:ate@equipacare.com.br`

            this.equipment = this.order.equipment

            if (this.order.category == 1) this.disableForm = true

            Promise.all([this.getTemplates(), this.getPatterns()])
          })
    },

    submit() {
      if (!this.$refs.form.validate()) return

      this.calibration.date = Utils.nowDatetimeFormat(this.date)

      if (this.isEditing) {
        const model = {
          ...this.calibration,
          serviceOrderId: this.order.id,
          parameters: this.parameters
            .filter((x) => !!x.patternResolution)
            .map((x) => {
              return {
                ...x,
                parameterId: x.parameterId,
                equipmentResolution: x.equipmentResolution
                  .toString()
                  .replace(',', '.'),
                ranges: x.interpolatedRanges.map((y) => {
                  return {
                    ...y,
                    range: y.range,
                    uncertainty: y.uncertainty,
                    tendency: y.tendency,
                    originalTendency: y.originalTendency,
                    vl1: y.vl1 ? y.vl1.toString().replace(',', '.') : null,
                    vl2: y.vl2 ? y.vl2.toString().replace(',', '.') : null,
                    vl3: y.vl3 ? y.vl3.toString().replace(',', '.') : null,
                    vl4: y.vl4 ? y.vl4.toString().replace(',', '.') : null,
                    vl5: y.vl5 ? y.vl5.toString().replace(',', '.') : null,
                  }
                }),
              }
            }),
        }

        this.loading = true
        this.apiService
          .put('calibration/v2/certified', model)
          .then((response) => {
            this.loading = false
            this.$toast.success('Certificado atualizada com sucesso!')
            this.$router.push('/calibration/v2/print?id=' + response.content.id)
          })
          .catch(() => {
            this.loading = false
          })
      } else {
        const model = {
          ...this.calibration,
          serviceOrderId: this.order.id,
          parameters: this.parameters
            .filter((x) => !!x.patternResolution)
            .map((x) => {
              return {
                ...x,
                parameterId: x.id,
                equipmentResolution: x.equipmentResolution
                  .toString()
                  .replace(',', '.'),
                ranges: x.interpolatedRanges.map((y) => {
                  return {
                    ...y,
                    range: y.range,
                    uncertainty: y.uncertainty,
                    tendency: y.tendency,
                    originalTendency: y.originalTendency,
                    vl1: y.vl1 ? y.vl1.replace(',', '.') : null,
                    vl2: y.vl2 ? y.vl2.replace(',', '.') : null,
                    vl3: y.vl3 ? y.vl3.replace(',', '.') : null,
                    vl4: y.vl4 ? y.vl4.replace(',', '.') : null,
                    vl5: y.vl5 ? y.vl5.replace(',', '.') : null,
                  }
                }),
              }
            }),
        }

        this.loading = true
        this.apiService
          .post('calibration/v2/certified', model)
          .then((response) => {
            this.loading = false
            this.$toast.success('Certificado cadastrado com sucesso!')

            const route = this.$router.resolve({
              path: '/calibration/v2/print?id=' + response.content.id,
            })
            window.open(route.href, '_blank')

            this.$router.push(`/editServiceOrder/${this.order.id}`)
          })
          .catch(() => {
            this.loading = false
          })
      }
    },

    removeRange(parameter, index) {
      this.confirmDialog = {
        visible: true,
        title: 'Confirmação',
        message: 'Tem certeza de que deseja remover esta faixa?',
        onConfirm: () => {
          parameter.interpolatedRanges.splice(index, 1)
          this.$toast.success('Faixa removida com sucesso!')
          this.confirmDialog.visible = false
        },
        onCancel: () => {
          this.confirmDialog.visible = false
        },
      }
    },
  },

  watch: {
    date() {
      this.setNextDate()
    },
  },
}
</script>

<style scoped></style>
